import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, HeartHandshake, Sunrise, School, Video, Library,
} from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { CTASection } from "@/components/CTASection";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { IMAGES } from "@/data/content";
import { useContent } from "@/context/ContentContext";
import CalEmbed from "@/components/CalEmbed";
import { CAL_LINKS } from "@/lib/calLinks";
import NotFound from "@/pages/NotFound";

const ICONS = { HeartHandshake, Sunrise, School, Video, Library };

export default function ServiceDetail() {
  const { slug } = useParams();
  const { services } = useContent();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <NotFound />;

  const Icon = ICONS[service.icon];

  return (
    <>
      <section className="container-site pb-14 pt-16 sm:pt-24" data-testid="service-detail-hero">
        <Link
          to="/services"
          data-testid="service-back-link"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors duration-300 hover:text-terracotta-500"
        >
          <ArrowLeft className="h-4 w-4" /> All services
        </Link>
        <div className="mt-8 grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-forest/10 text-forest dark:bg-sage/10 dark:text-sage">
              <Icon className="h-7 w-7" />
            </span>
            <h1 className="mt-6 font-heading text-4xl font-semibold leading-[1.08] tracking-tight text-foreground sm:text-5xl">
              <MaskedLine delay={0.1}>{service.title}</MaskedLine>
            </h1>
            <Reveal delay={0.25}>
              <p className="mt-3 text-xs font-medium uppercase tracking-[0.2em] text-terracotta-500">
                {service.tagline}
              </p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
                {service.short}
              </p>
              <Link to="/book" data-testid="service-book-button" className="btn-primary mt-8">
                Book a Consultation <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <img
              src={IMAGES.consultation}
              alt={`${service.title} at Evol & Troppus`}
              className="h-64 w-full rounded-3xl object-cover shadow-lift lg:h-80 dark:shadow-soft-dark"
              loading="lazy"
            />
          </Reveal>
        </div>
      </section>

      <section className="container-site pb-24" data-testid="service-overview-section">
        <Reveal>
          <div className="rounded-3xl border border-border bg-card p-9 shadow-soft sm:p-12">
            <h2 className="font-heading text-2xl font-semibold text-foreground">Overview</h2>
            {service.overview.map((p, i) => (
              <p key={i} className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground">
                {p}
              </p>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="border-y border-border bg-card py-24" data-testid="service-benefits-section">
        <div className="container-site grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <SectionHeader overline="Benefits" title="What you'll walk away with" />
              <ul className="mt-8 space-y-4">
                {service.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`service-benefit-${i + 1}`}>
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-forest/10 text-forest dark:bg-terracotta-500/15 dark:text-terracotta-400">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-sm leading-relaxed text-foreground/90 md:text-base">{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <SectionHeader overline="Who it's for" title="This service fits if you are…" />
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {service.whoFor.map((w, i) => (
                  <div
                    key={i}
                    data-testid={`service-who-for-${i + 1}`}
                    className="rounded-2xl border border-border bg-background p-5 text-sm leading-relaxed text-foreground/90"
                  >
                    {w}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {CAL_LINKS[service.slug] && (
        <section className="container-site pb-24" data-testid="service-booking-section">
          <div className="mx-auto max-w-4xl">
            <Reveal>
              <SectionHeader
                overline="Book this service"
                title={`Schedule ${service.title}`}
                description="Choose a time below — you'll receive a confirmation email right after booking."
                align="center"
              />
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-10">
                <CalEmbed
                  namespace={CAL_LINKS[service.slug].namespace}
                  calLink={CAL_LINKS[service.slug].calLink}
                />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="container-site py-24" data-testid="service-faq-section">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <SectionHeader overline="Questions" title="Frequently asked" align="center" />
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10 w-full" data-testid="service-faq-accordion">
              {service.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-border">
                  <AccordionTrigger
                    data-testid={`service-faq-trigger-${i + 1}`}
                    className="text-left font-heading text-base font-medium text-foreground hover:text-terracotta-500 hover:no-underline"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CTASection
        title={`Ready to explore ${service.title.toLowerCase()}?`}
        subtitle="Book a consultation and let's talk about what meaningful change could look like for you."
      />
    </>
  );
}
